/**
 * IM RTC Provider integration.
 *
 * Implements `contracts/rtc-provider.json` exactly:
 *  - `getRTCTokenInfo({ channelName, uid?, role? }) -> { appId, rtcToken, rtcUid, serverUrl? }`
 *  - `getUserIdsWithRTCUids(rtcUids: number[]) -> Record<string, string>`
 * The two methods are configured and fall back independently: a broken uid mapping must never stop
 * a call from joining.
 */

export interface RtcTokenInfo {
  appId: string;
  rtcToken: string;
  rtcUid: number;
  serverUrl?: string;
}

export interface RtcProvider {
  getRTCTokenInfo(input: {
    channelName: string;
    uid?: number;
    role?: 'host' | 'audience';
  }): Promise<RtcTokenInfo>;
  getUserIdsWithRTCUids(rtcUids: number[]): Promise<Record<string, string>>;
}

export interface ProviderEndpoints {
  tokenEndpoint: string | null;
  uidEndpoint: string | null;
}

/** Deterministic offline provider: tokens and uids are derived from the channel/uid inputs. */
export class MockImProvider implements RtcProvider {
  constructor(private readonly options: { appId?: string; serverUrl?: string | null } = {}) {}

  async getRTCTokenInfo(input: {
    channelName: string;
    uid?: number;
    role?: 'host' | 'audience';
  }): Promise<RtcTokenInfo> {
    const uid = input.uid ?? deriveUid(input.channelName);
    const payload = btoa(
      JSON.stringify({ ch: input.channelName, uid, role: input.role ?? 'host', v: 'mock' }),
    );
    return {
      appId: this.options.appId ?? 'demo-app-id',
      rtcToken: `mock.${payload}.signature`,
      rtcUid: uid,
      ...(this.options.serverUrl ? { serverUrl: this.options.serverUrl } : {}),
    };
  }

  async getUserIdsWithRTCUids(rtcUids: number[]): Promise<Record<string, string>> {
    return Object.fromEntries(rtcUids.map((uid) => [String(uid), `user-${uid}`]));
  }
}

/** HTTP provider matching the two documented IM endpoints. */
export class HttpImProvider implements RtcProvider {
  constructor(
    private readonly endpoints: ProviderEndpoints,
    // `this.fetchImpl(...)` would otherwise call native fetch with the provider as `this`, which
    // browsers reject with "Illegal invocation". Bind the default once; injected mocks are unaffected.
    private readonly fetchImpl: typeof fetch = fetch.bind(globalThis),
  ) {}

  async getRTCTokenInfo(input: {
    channelName: string;
    uid?: number;
    role?: 'host' | 'audience';
  }): Promise<RtcTokenInfo> {
    if (!this.endpoints.tokenEndpoint) throw new Error('IM token endpoint is not configured');
    const query = new URLSearchParams({ channelName: input.channelName });
    if (input.uid !== undefined) query.set('uid', String(input.uid));
    if (input.role) query.set('role', input.role);
    const separator = this.endpoints.tokenEndpoint.includes('?') ? '&' : '?';
    const response = await this.fetchImpl(
      `${this.endpoints.tokenEndpoint}${separator}${query.toString()}`,
      {
        headers: { accept: 'application/json' },
      },
    );
    if (!response.ok) throw new Error(`IM token endpoint returned ${response.status}`);
    const body = (await response.json()) as Partial<RtcTokenInfo>;
    if (
      typeof body.appId !== 'string' ||
      typeof body.rtcToken !== 'string' ||
      typeof body.rtcUid !== 'number'
    ) {
      throw new Error('IM token endpoint returned an invalid payload');
    }
    return body as RtcTokenInfo;
  }

  async getUserIdsWithRTCUids(rtcUids: number[]): Promise<Record<string, string>> {
    if (!this.endpoints.uidEndpoint) throw new Error('IM uid endpoint is not configured');
    const response = await this.fetchImpl(this.endpoints.uidEndpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ rtcUids }),
    });
    if (!response.ok) throw new Error(`IM uid endpoint returned ${response.status}`);
    const body = (await response.json()) as { mapping?: Record<string, string> } & Record<
      string,
      string
    >;
    const mapping = body.mapping ?? (body as Record<string, string>);
    return Object.fromEntries(
      Object.entries(mapping).filter(([, value]) => typeof value === 'string'),
    );
  }
}

/**
 * Ordered fallback for the two methods independently. Token resolution tries each provider until
 * one returns; uid mapping does the same and degrades to `{}` (identity echo) rather than failing
 * the meeting.
 */
export class FallbackRtcProvider implements RtcProvider {
  constructor(private readonly providers: RtcProvider[]) {}

  async getRTCTokenInfo(input: {
    channelName: string;
    uid?: number;
    role?: 'host' | 'audience';
  }): Promise<RtcTokenInfo> {
    const errors: string[] = [];
    for (const provider of this.providers) {
      try {
        return await provider.getRTCTokenInfo(input);
      } catch (error) {
        errors.push(error instanceof Error ? error.message : String(error));
      }
    }
    throw new Error(
      `no IM RTC provider could issue a token (${errors.join('; ') || 'none configured'})`,
    );
  }

  async getUserIdsWithRTCUids(rtcUids: number[]): Promise<Record<string, string>> {
    for (const provider of this.providers) {
      try {
        const mapping = await provider.getUserIdsWithRTCUids(rtcUids);
        if (Object.keys(mapping).length > 0) return mapping;
      } catch {
        // mapping is decorative; continue with the next provider
      }
    }
    return {};
  }
}

/** Stable 31-bit Agora uid derived from the meeting number (no collision within a channel). */
export function deriveUid(seed: string): number {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  const value = (hash >>> 0) % 2147483647;
  return value === 0 ? 1 : value;
}

/** Meeting-level IM concerns on top of the RTC provider (members, invites). */
export interface ImMeetingBridge {
  listMembers(channelName: string): Promise<Array<{ userId: string; displayName: string }>>;
  invite(channelName: string, userIds: string[]): Promise<void>;
}

/** Local bridge used by the offline demo and tests. */
export class LocalImMeetingBridge implements ImMeetingBridge {
  private readonly members = new Map<string, Array<{ userId: string; displayName: string }>>();
  private readonly invitations: Array<{ channelName: string; userIds: string[]; at: string }> = [];

  async listMembers(channelName: string): Promise<Array<{ userId: string; displayName: string }>> {
    return this.members.get(channelName) ?? [];
  }

  async invite(channelName: string, userIds: string[]): Promise<void> {
    this.invitations.push({ channelName, userIds, at: new Date().toISOString() });
  }

  addMember(channelName: string, member: { userId: string; displayName: string }): void {
    const list = this.members.get(channelName) ?? [];
    if (!list.some((entry) => entry.userId === member.userId)) list.push(member);
    this.members.set(channelName, list);
  }

  sentInvitations(): ReadonlyArray<{ channelName: string; userIds: string[]; at: string }> {
    return this.invitations;
  }
}
